import { HANDLEBARS_REGEX, MULTI_WORD_HANDLEBARS_REGEX } from "@avenueops/frontend-shared";
import { ClientMappingField, ClientMappingValue } from "@avenueops/shared-types";
import { TableCellsIcon, VariableIcon } from "@heroicons/react/24/outline";
import Card from "components/generic-building-blocks/Card";
import RadioGroupList from "components/generic-building-blocks/RadioGroupList";
import UncontrolledInputField from "components/inputs/UncontrolledInputField";
import UncontrolledInputSearchableSelect from "components/inputs/UncontrolledInputSearchableSelect";
import UncontrolledInputTextarea from "components/inputs/UncontrolledInputTextarea";
import { useEnrichmentColumns, useQueryColumns } from "context/QueryRunContext";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useOutsideClick } from "utils/hooks";

interface Props {
  update: (_: any) => void;
  item: ClientMappingField | ClientMappingValue;
  isFirst: boolean;
  prefix: string;
  error?: boolean;
  disabled?: boolean;
}

enum MappingFieldType {
  TEXT = "TEXT",
  TEMPLATE = "TEMPLATE",
  COLUMN = "COLUMN",
}

export default function MappingValueInput({ update, isFirst, item, error, prefix, disabled }: Props) {
  const { watch } = useFormContext();

  const queryColumns = useQueryColumns();
  const enrichmentColumns = useEnrichmentColumns();
  const columnNames = [...queryColumns, ...enrichmentColumns];
  const columnOptions = columnNames.map((key: any) => ({ value: key, label: key }));

  const [editingField, setEditingField] = useState<boolean>(false);
  const editingFieldRef = useRef<HTMLDivElement>(null);
  const [fieldType, setFieldType] = useState<string>(MappingFieldType.TEXT);
  useOutsideClick(editingFieldRef, () => setEditingField(false));

  const removeEmbeddedDataFormatting = (value: string) => {
    if (!HANDLEBARS_REGEX.test(value)) {
      return value;
    }
    if (MULTI_WORD_HANDLEBARS_REGEX.test(value)) {
      return value.slice(3, -3);
    }
    return value.slice(2, -2);
  };

  const getEmbeddedDataFormatting = (value: string) => {
    return value ? `{{[${value}]}}` : "";
  };

  const itemValue = watch(`${prefix}.value`);

  return item.type === "VALUE" ? (
    <div className="w-auto relative">
      <div onClick={() => setEditingField(!editingField)}>
        <UncontrolledInputField title="Value" className="cursor-pointer overflow-ellipsis" value={itemValue} disabled={editingField} onChange={() => {}} />
      </div>
      {editingField ? (
        <div ref={editingFieldRef}>
          <Card className="flex flex-col w-96 items-center gap-4 absolute bottom-8 right-20 p-4 bg-white shadow-md rounded">
            <RadioGroupList
              padding="p-2"
              value={fieldType}
              setValue={setFieldType}
              settings={[
                {
                  value: MappingFieldType.TEXT,
                  title: "Text",
                  icon: <div className="text-gray-500 font-medium text-xs self-center pr-1">abc</div>,
                },

                {
                  value: MappingFieldType.TEMPLATE,
                  title: "Template",
                  icon: <VariableIcon className="text-gray-500 h-4 w-4 self-center mr-1 -ml-3" />,
                },
                {
                  value: MappingFieldType.COLUMN,
                  icon: <TableCellsIcon className="text-gray-500 h-4 w-4 self-center mr-1 -ml-3" />,
                  title: "Column",
                },
              ]}
            />
            <div className="h-[100px] w-full">
              {fieldType === MappingFieldType.TEXT ? (
                <UncontrolledInputTextarea
                  placeholder="Enter raw text"
                  value={item.value}
                  onChange={(value: string) => update({ ...item, value: value ?? "" })}
                  error={error}
                  disabled={!!disabled}
                  className="w-[22rem] max-w-full"
                />
              ) : fieldType === MappingFieldType.COLUMN ? (
                columnOptions.length > 0 ? (
                  <UncontrolledInputSearchableSelect
                    options={columnOptions}
                    val={removeEmbeddedDataFormatting(item.value)}
                    disabled={!!disabled}
                    className="w-[22rem]"
                    onChange={(value: string) => update({ ...item, value: getEmbeddedDataFormatting(value) })}
                    placeholder="Enter or select..."
                  />
                ) : (
                  <div className="text-sm leading-5 text-gray-500 font-medium">No columns available</div>
                )
              ) : null}
              {fieldType === MappingFieldType.TEMPLATE ? (
                <div className="flex flex-row gap-2 w-full items-center">
                  <span className="font-medium text-2xl text-red-400 self-start">{`{{`}</span>
                  <UncontrolledInputTextarea
                    placeholder="Enter embedded data"
                    value={removeEmbeddedDataFormatting(item.value)}
                    onChange={(value: string) => update({ ...item, value: getEmbeddedDataFormatting(value) })}
                    error={error}
                    disabled={!!disabled}
                    className="w-[20rem] max-w-full bg-gray-50 bg-opacity-70"
                  />
                  <span className="font-medium text-2xl text-red-400 self-start">{`}}`}</span>
                </div>
              ) : null}
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  ) : null;
}
